import * as Linking from 'expo-linking';
import * as WebBrowser from 'expo-web-browser';

/**
 * Native Stripe Checkout hand-back (#290).
 *
 * Stripe only accepts an HTTPS return URL, so a native checkout cannot be sent
 * straight to `alldrivers://`. The working pattern — already used by the owner
 * flow — is dual-URL:
 *
 *   1. Stripe returns to our backend `stripe-redirect` (HTTPS);
 *   2. `stripe-redirect` 302s to the app's deep link;
 *   3. `openAuthSessionAsync` is watching THAT deep link, so the OS resolves
 *      the auth session and closes the in-app browser.
 *
 * Driver self-pay used to pass an HTTPS web URL as the second argument, so the
 * session never resolved to the app and the browser stayed open until the user
 * dismissed it by hand. This helper exists so the two call sites cannot drift
 * apart again — the deep link the browser watches is, by construction, the same
 * one the server was told to redirect to.
 *
 * Deliberately NOT `dismissBrowser()` in a `finally`: that fires before the
 * redirect lands and makes a cancellation indistinguishable from a success.
 */

export interface NativeStripeCheckoutResult {
  /** Raw result type from expo-web-browser. */
  type: WebBrowser.WebBrowserAuthSessionResult['type'];
  /** Parsed query parameters of the deep link, empty when there was none. */
  params: Record<string, string>;
  /** The deep link the OS handed back, when the session resolved. */
  url?: string;
}

const firstValue = (value: string | string[] | undefined | null): string | undefined => {
  if (Array.isArray(value)) return value[0];
  return value ?? undefined;
};

/** The deep link this app should be returned to for a given route. */
export const checkoutDeepLink = (path: string): string => Linking.createURL(path);

/**
 * Opens hosted Stripe Checkout and waits for the app's own deep link.
 *
 * `deepLink` MUST be the value handed to the server as `frontend_return_url`,
 * otherwise the session watches one URL while the server redirects to another
 * and the browser never closes.
 */
export const openNativeStripeCheckout = async ({
  checkoutUrl,
  deepLink,
}: {
  checkoutUrl: string;
  deepLink: string;
}): Promise<NativeStripeCheckoutResult> => {
  const result = await WebBrowser.openAuthSessionAsync(checkoutUrl, deepLink);
  if (result.type !== 'success' || !result.url) {
    return { type: result.type, params: {} };
  }
  const params: Record<string, string> = {};
  try {
    const { queryParams } = Linking.parse(result.url);
    Object.entries(queryParams ?? {}).forEach(([key, value]) => {
      const single = firstValue(value as string | string[] | null);
      if (single != null) params[key] = single;
    });
  } catch {
    // A malformed callback carries no usable settle state; fail closed by
    // returning no params, which the callers treat as "unknown outcome".
  }
  return { type: result.type, params, url: result.url };
};

/**
 * Settle state for the self-pay checkout, read ONLY from what the server
 * authored into the redirect. Anything else is "unknown", which the return
 * screen resolves against the authoritative paywall status.
 */
export const selfPaySettleFromResult = (
  result: NativeStripeCheckoutResult,
): 'success' | 'canceled' | 'unknown' => {
  if (result.type !== 'success') return 'canceled';
  const settle = result.params.self_pay;
  if (settle === 'success' || settle === 'canceled') return settle;
  return 'unknown';
};
